/**
 * Severity presentation for the Dependency-Track mirror.
 *
 * DT's severity vocabulary is mapped to the chip tones of the function-colour
 * scale (Kritisch/Hoch → Alarm Rot, Mittel → Signalgelb, Niedrig → Flieder,
 * Info → neutral). UNASSIGNED is deliberately NOT rendered as harmless: it means DT has no
 * CVSS vector, which is common for the OT advisories that matter most here.
 */

import type { ChipTone } from '../../components/Chip';
import type { DtSeverity, Tone } from '../../lib/types';

export const SEVERITY_LABELS: Readonly<Record<DtSeverity, string>> = {
  CRITICAL: 'Kritisch',
  HIGH: 'Hoch',
  MEDIUM: 'Mittel',
  LOW: 'Niedrig',
  INFO: 'Info',
  UNASSIGNED: 'Ohne Bewertung',
};

export const SEVERITY_TONE: Readonly<Record<DtSeverity, ChipTone>> = {
  CRITICAL: 'alert',
  HIGH: 'alert',
  MEDIUM: 'warn',
  LOW: 'info',
  INFO: 'neutral',
  UNASSIGNED: 'warn',
};

/** Rank for sorting; unknown values sort last. */
export function severityRank(value: string | null | undefined): number {
  const order: readonly string[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO', 'UNASSIGNED'];
  const index = order.indexOf(String(value ?? '').toUpperCase());
  return index < 0 ? order.length : index;
}

/** German label for the four project states. */
export const SCAN_STATE_LABELS: Readonly<Record<string, string>> = {
  ok: 'Gescannt',
  ausstehend: 'Scan läuft',
  'kein-projekt': 'Kein SBOM',
  unbekannt: 'Unbekannt',
};

export const SCAN_STATE_TONE: Readonly<Record<string, Tone>> = {
  ok: 'good',
  ausstehend: 'warn',
  'kein-projekt': 'alert',
  unbekannt: 'neutral',
};

/** EPSS as a percentage with one decimal, or the dash. */
export function fmtEpss(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return `${(value * 100).toFixed(1)} %`;
}
